import {all, call, put, select, takeEvery} from "redux-saga/effects";
import {
    ActionRequiredAlertsEntry,
    DoctorsDashboardCareNoteHttpClient,
    IFileResponse,
    IGetPatientInformationResponse,
    IListCareNotesResult,
    IListCareNotesResultEntry,
    DoctorsDashboardDailyReportsHttpClient,
    InsightImage,
} from "doctors-dashboard/http-clients/index";
import {ORPYXSI_API_URL} from "../../../../../../appConstants";
import {PatientInformationPageSelectors} from "../../patient-information-page/store/patient-information-page.selectors";
import {
    DOWNLOAD_INSIGHT_FIGURE_SNAPSHOT_FAILED,
    DOWNLOAD_INSIGHT_FIGURE_SNAPSHOT_SUCCESSFULLY,
    DOWNLOAD_PATIENT_CARE_NOTE_REPORT_FAILED,
    DOWNLOAD_PATIENT_CARE_NOTE_REPORT_SUCCESSFULLY,
    GET_PATIENT_CARE_NOTES_FAILED,
    GET_PATIENT_CARE_NOTES_SUCCESSFULLY,
    PatientActivityLogActions,
    PROBE_INSIGHT_FIGURE_SNAPSHOT_AS_OF,
    PROBE_INSIGHT_FIGURE_SNAPSHOT_AS_OF_FAILED,
    PROBE_INSIGHT_FIGURE_SNAPSHOT_AS_OF_SUCCESSFULLY
} from "./patient-activity-log.actions";
import {PatientActivityLogSelectors} from "./patient-activity-log.selectors";
import {base64ToBlob, DateRange, DateTimePipe, ENQUEUE_SNACKBAR, OperationErrorPipe, OperationErrorType, StoreAction} from "orpyx-web-common";
import FileSaver from "file-saver";
import StreamSaver from "streamsaver";
import { DailyInsightsApiService } from "../../patient-insights-api/patient-insights-api.service";
import { PatientInformationDailyInsightsSelectors } from "../../patient-information-page/components/patient-information-daily-insights/store/patient-information-daily-insights.selectors";
import { IActivityLogCareNote } from "../components/patient-activity-log/patient-activity-log.interface";

const API_VERSION = '1';

function* initializeInsightSnapshot(action: StoreAction<PatientActivityLogActions, IListCareNotesResultEntry>): Generator<any, any, any> {

    const patient = yield select(PatientInformationDailyInsightsSelectors.patient);

    try {
        const apiService = DailyInsightsApiService.getInstance();

        if (!apiService) {
            throw new Error("Failed to initialize Daily Insights API service");
        }
        if (!action.payload) {
            throw new Error("Unable to determine date for which to produce snapshot.");
        }


        const asOf = action.payload?.noteDate;
        if (!asOf) {
            yield put(PROBE_INSIGHT_FIGURE_SNAPSHOT_AS_OF_FAILED(new Error('Unable to retreive historical insight snapshot')));
        }


        const dateString = asOf?.toISOString().slice(0, 10);

        const response: InsightImage[] = yield call([apiService, apiService.getPatientInsights], patient.patientId, dateString, 1, true, Math.floor(asOf!.valueOf() / 1000));

        if (response.length !== 1) {
            throw new Error('Unable to retreive historical insight snapshot');
        }

        else {
            const snapshotUrl = response[0].url;

            if (snapshotUrl) {
                yield put(PROBE_INSIGHT_FIGURE_SNAPSHOT_AS_OF_SUCCESSFULLY(action.payload))
            } else {
                throw new Error('Invalid response from patient insight API');
            }
        }
    } catch (error) {
        yield put(PROBE_INSIGHT_FIGURE_SNAPSHOT_AS_OF_FAILED(error as Error));
    }
}

function* onDownloadPatientCareNoteReport(action: StoreAction<PatientActivityLogActions, IListCareNotesResultEntry>) {
    const careNote = action.payload!;

    try {
        const patient: IGetPatientInformationResponse = yield select(PatientInformationPageSelectors.patient)!;

        const api = new DoctorsDashboardDailyReportsHttpClient(ORPYXSI_API_URL);

        const data: IFileResponse = yield call(
            [api, api.getPatientActivityLogReport],
            patient.patientId!,
            careNote.relatedReportName!,
            API_VERSION,
        );

        yield put(DOWNLOAD_PATIENT_CARE_NOTE_REPORT_SUCCESSFULLY(careNote.rowMarker!));
        yield put(ENQUEUE_SNACKBAR({
            message: `Successfully downloaded activity report at ${DateTimePipe(careNote.noteDate)}`,
            options: {variant: "success"}
        }));

        const blob = base64ToBlob(data.content!, data.contentType!);
        FileSaver.saveAs(blob, data.fileName!);
    } catch (e) {
        yield put(DOWNLOAD_PATIENT_CARE_NOTE_REPORT_FAILED(careNote.rowMarker!, e as Error));

        yield put(ENQUEUE_SNACKBAR({
            message: `Failed download activity report at ${DateTimePipe(careNote.noteDate)}, error: ${OperationErrorPipe(e as Error)}`,
            options: {variant: "error"}
        }));
    }
}

function* onDownloadPatientInsightFigure(action: StoreAction<PatientActivityLogActions, {careNote: IActivityLogCareNote} >): Generator<any, any, any> {
    try {
        if (!action.payload) {
            throw new Error('Could not determine URL to download insight figure.');
        } else {
            const patient = yield select(PatientInformationDailyInsightsSelectors.patient);
            const apiService = DailyInsightsApiService.getInstance();

            if (!apiService) {
                throw new Error("Failed to initialize Daily Insights API service");
            }
            const asOf = action.payload.careNote.noteDate;
            if (!asOf) { throw new Error("Unable to determine date for which to produce snapshot."); }
                
            const dateString = new Date(asOf.valueOf()).toISOString().slice(0, 10);
            const response: InsightImage[] = yield call([apiService, apiService.getPatientInsights], patient.patientId, dateString, 1, true, asOf.unix());

            if (response.length !== 1 || !response[0].url) {
                throw new Error("Unable to determine insight figure URL.")
            }

            const result: Response = yield fetch(response[0].url)
            if (!result.ok) throw new Error('Unable to download image data. check your internet connection.')
            
            const fileStream = StreamSaver.createWriteStream(action.payload.careNote.noteDate!.format("YYYY-MM-DD,hh-ss.png"));
            yield result.body?.pipeTo(fileStream);           
            yield put(DOWNLOAD_INSIGHT_FIGURE_SNAPSHOT_SUCCESSFULLY(action.payload.careNote.rowMarker!))
            
        }

    } catch (e) {
        yield put(DOWNLOAD_INSIGHT_FIGURE_SNAPSHOT_FAILED(action.payload?.careNote?.rowMarker!, e as Error));
        yield put(ENQUEUE_SNACKBAR({
            message: `Failed download insight figure as of ${DateTimePipe(action.payload?.careNote.noteDate)}, error: ${OperationErrorPipe(e as Error)}`,
            options: {variant: "error"}
        }));
    }
}

function* onFetchPatientCareNotes() {
    try {
        const patient: IGetPatientInformationResponse = yield select(PatientInformationPageSelectors.patient)!;
        const lastRowMarker: string | undefined = yield select(PatientActivityLogSelectors.lastRowMarker)!;
        const takeCount: number = yield select(PatientActivityLogSelectors.takeCount)!;

        const monitoringCycle: DateRange = yield select(PatientActivityLogSelectors.monitoringCycle)!;
        const api = new DoctorsDashboardCareNoteHttpClient(ORPYXSI_API_URL);

        const data: IListCareNotesResult = yield call(
            [api, api.searchPatientCareNotes],
            patient.patientId!,
            takeCount,
            lastRowMarker,
            //  TODO: Fix types
            monitoringCycle.begin! as any,
            monitoringCycle.end!  as any,
            API_VERSION
        );

        const safeCareNotes: IListCareNotesResultEntry[] = data.careNotes!.map(careNote => ({
            ...careNote,    //  Need for fix duplicate zero (0) key for action required alerts (not filled by back-end after load from azure)
            actionRequiredAlerts: careNote.actionRequiredAlerts?.map((alert, index) => new ActionRequiredAlertsEntry({
                ...alert,
                id: parseInt(careNote.rowMarker!) + index,
            }))
        }));

        yield put(GET_PATIENT_CARE_NOTES_SUCCESSFULLY(safeCareNotes));
    } catch (e) {
        yield put(GET_PATIENT_CARE_NOTES_FAILED(e as Error));
    }
}

function* onFetchPatientCareNotesSuccess(action: StoreAction<PatientActivityLogActions, IListCareNotesResultEntry[]>): Generator<any, any, any> {
    try {
        if (!action.payload) {
            throw new Error('Could not determine URL to download insight figure.');
        } else {

            yield all(action.payload.map(
                item => 
                    call(
                        initializeInsightSnapshot, 
                        {
                            payload: item,
                            type: PatientActivityLogActions.PROBE_INSIGHT_FIGURE_SNAPSHOT_AS_OF
                        }
            )))
        }
    } catch (e) {
        
    }
}

function* onAppendPatientCareNote(action: StoreAction<PatientActivityLogActions, IListCareNotesResultEntry>): Generator<any, any, any> {
    if (!action.payload) {
        throw new Error('Could not process newly added care note.');
    }
    yield put(PROBE_INSIGHT_FIGURE_SNAPSHOT_AS_OF(action.payload));
}
export default function* PatientActivityLogSagas(): Generator<any, any, any> {
    yield all([
        yield takeEvery(PatientActivityLogActions.DOWNLOAD_PATIENT_CARE_NOTE_REPORT, onDownloadPatientCareNoteReport),
        yield takeEvery(PatientActivityLogActions.GET_PATIENT_CARE_NOTES, onFetchPatientCareNotes),
        yield takeEvery(PatientActivityLogActions.GET_PATIENT_CARE_NOTES_SUCCESSFULLY, onFetchPatientCareNotesSuccess),
        yield takeEvery(PatientActivityLogActions.PROBE_INSIGHT_FIGURE_SNAPSHOT_AS_OF, initializeInsightSnapshot),
        yield takeEvery(PatientActivityLogActions.DOWNLOAD_INSIGHT_FIGURE_SNAPSHOT, onDownloadPatientInsightFigure),
        yield takeEvery(PatientActivityLogActions.APPEND_PATIENT_CARE_NOTE, onAppendPatientCareNote),
    ]);
}